package com.neurobs.js.ccab

import com.neurobs.ccab.IdentityData
import com.neurobs.errors.{Result, ThrowableErrorReport}
import com.neurobs.js.aws.Cognito

import org.scalajs.dom.{StorageEvent, console, document, window}
import org.scalajs.dom.window.localStorage

import scala.concurrent.Future
import scala.concurrent.ExecutionContext.Implicits.global
import scala.scalajs.js
import scala.util.control.NonFatal

class UserManager private (private var user: Option[IdentityData]) {
	private var subscriber: Option[Option[IdentityData] => Unit] = None

	def hasUser: Boolean = user.isDefined

	def getUser: Option[IdentityData] = user

	def setChangeSubscriber(callback: Option[IdentityData] => Unit): Unit = {
		//println("UserManager.setChangeSubscriber")
		subscriber = Some(callback)
	}

	private def setUser(newUser: Option[IdentityData]): Unit = {
		if (newUser != user) {
			user = newUser
			subscriber.foreach(_(newUser))
		}
	}

	/** Updates the user state from the local Cognito library.
	 *  If there are local unexpired tokens, Cognito is not called unless forceRefresh is true.
	 *  If there are local expired tokens, a token refresh will be attempted regardless of forceRefresh.
	 *  If forceRefresh is true, a token refresh will always be attempted.
	 *  If the refresh is rejected by the service (e.g. expired refresh token), the stored login is cleared even
	 *  if the current tokens are unexpired. If the refresh fails for another reason (e.g.
	 *  network error), the stored login is kept and this returns a Left.*/
	def update(forceRefresh: Boolean = false): Future[Result[Unit]] = {
		UserManager.update(forceRefresh) map {
			case Right(newUser) =>
				setUser(newUser)
				Right(())
			case Left(error) =>
				// The session state is unknown (e.g. network error); keep the
				// current user and cookie rather than treating it as a sign-out.
				console.warn(s"UserManager.update failed: ${error.description}")
				Left(error)
		}
	}

	def logout(): Future[Unit] = {
		Cognito.signOut()
	}
}

object UserManager {
	def apply(): Future[UserManager] = {
		update(forceRefresh = false) map { result =>
			// On a transient failure, start with no user. The cookie is left
			// alone since it may still hold a valid id token; a later
			// successful update will pick the session back up.
			val initialUser = result match {
				case Right(u) => u
				case Left(_) => None
			}
			val m = new UserManager(initialUser)
			Cognito.subscribeToChange { () =>
				m.update()
			}
			window.addEventListener("storage", (e: StorageEvent) => {
				// oldValue/newValue are null (read as undefined) when the key is
				// added or removed; guard before comparing.
				if ((e.key == idCookieName) && !js.isUndefined(e.oldValue) && !js.isUndefined(e.newValue) && (e.oldValue != e.newValue)) {
					m.update()
				}
			})
			m
		}
	}

	private def update(forceRefresh: Boolean): Future[Result[Option[IdentityData]]] = {
		Cognito.currentUser(forceRefresh) map {
			case None =>
				// Definitive: Amplify has no valid session (signed out, or the
				// refresh token was rejected and the tokens were cleared).
				deleteCookie()
				Right(None)
			case Some(data) =>
				setCookie(data.idToken)
				Right(Some(data.user))
		} recover {
			case NonFatal(e) => Left(ThrowableErrorReport(e))
		}
	}

	private val idCookieName = "CCAB_ID_TOKEN"

	/** Store the token in a cookie, and also in local storage to trigger
	 * notifications in other tabs that might be open with the same app  */
	private def setCookie(idToken: String): Unit = {
		document.cookie = s"$idCookieName=$idToken"
		localStorage.setItem(key = idCookieName, data = idToken)
	}
	/** Deletes the token cookie, and also in local storage to trigger
	 * notifications in other tabs that might be open with the same app  */
	private def deleteCookie(): Unit = {
		document.cookie = s"$idCookieName=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;"
		localStorage.removeItem(key = idCookieName)
	}
}